/**
 * Open questions IT has asked about this ticket.
 *
 * These are the reason a ticket sits still, so they render above the activity
 * trail as a warning `Alert` with the answer box already open — not as another
 * line in the timeline that the employee has to notice.
 *
 * This is the one write surface that argues *for* a file rather than merely
 * allowing one, and the only one that turns the press-again prompt on.
 *
 * The distinction is who is asking. A comment is someone volunteering an update
 * and gets no prompt at all — interrogating a volunteer is how you stop getting
 * updates. An information request is the opposite: IT is already blocked and
 * has explicitly asked, the ticket is stalled against a stated due date, and a
 * screenshot is usually the fastest possible answer to the question that was
 * just put. That is what earns the one extra press here and nowhere else.
 *
 * It still only ever costs one press. Someone who genuinely has nothing to send
 * presses again and the answer goes.
 */

import { Alert, Typography } from "@norican/react";

import { RelativeTime } from "@/components/common";
import { useLanguage, useT } from "@/i18n";

import type { TicketInfoRequest } from "../api/contract";
import { useAnswerInfoRequest } from "../api/queries";
import { useFeedbackToast } from "../lib/use-feedback-toast";
import { ComposeForm } from "./compose-form";
import { TranslatableTextView } from "./translatable-text";

export interface TicketInfoRequestsProps {
  ticketId: string;
  requests: TicketInfoRequest[];
  /** False when `ANSWER_INFO_REQUEST` is not permitted — read-only rendering. */
  canAnswer: boolean;
}

export function TicketInfoRequests({ ticketId, requests, canAnswer }: TicketInfoRequestsProps) {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const toast = useFeedbackToast();
  const mutation = useAnswerInfoRequest(ticketId);

  const open = requests.filter((request) => !request.answered);
  if (open.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {open.map((request) => (
        <Alert key={request.info_request_id} tone="warning" title={t("infoRequest.title")}>
          <div className="flex flex-col gap-3">
            <TranslatableTextView value={request.question} multiline />

            <Typography variant="caption" tone="muted">
              {t("infoRequest.askedBy", { name: request.asked_by.display_name })}
              {request.due_at === null ? null : (
                <>
                  {" · "}
                  {t("infoRequest.dueLabel")} <RelativeTime value={request.due_at} />
                </>
              )}
            </Typography>

            {canAnswer ? (
              <ComposeForm
                name={`info-${request.info_request_id}`}
                label={t("infoRequest.answer")}
                placeholder={t("infoRequest.placeholder")}
                submitLabel={t("infoRequest.send")}
                pending={mutation.isPending}
                error={mutation.error}
                attachments={{
                  label: t("infoRequest.attachLabel"),
                  hint: t("infoRequest.attachHint"),
                  nudge: true,
                }}
                onSubmit={async (body, attachmentIds) => {
                  const detail = await mutation.mutateAsync({
                    infoRequestId: request.info_request_id,
                    body: { body, body_lang: language, attachment_ids: attachmentIds },
                  });
                  toast.success(t("infoRequest.submitted"));
                  return detail;
                }}
              />
            ) : null}
          </div>
        </Alert>
      ))}
    </div>
  );
}
